<template>
    <div class="page-content-container">
        <h1 class="title">
            {{ $t('app.ask.estimate.title') }}
            <ReglePastille cle="demandeDevis.definition" />
            <ReglePastille v-if="step === EstimateFormStep.first" cle="demandeDevis.siteObligatoire" />
            <ReglePastille v-if="isEditing" cle="demandeDevis.siteNonModifiable" />
        </h1>
        <div class="subtitle-site" v-if="step === EstimateFormStep.second">
            <div>{{ selectedSite?.description ?? '' }}</div>
        </div>
        <div class="second-step">
            <div v-if="step === EstimateFormStep.second" class="estimate-content-container estimate-form">
                <h2 class="form-title">
                    {{ $t('app.ask.estimate.form.title') }}
                </h2>
                <p class="subtitle">{{ $t('app.ask.estimate.required') }}</p>
                <EstimateForm />
            </div>
            <SelectSite
                v-else
                :selectedSite="selectedSite"
                :title="$t('siteSelection.searchBar.title.estimate')"
                regle-cle="demandeDevis.listeSites"
                class="estimate-content-container"
                @pick="selectSite"
                @filter-change="selectSite(undefined)"
            />
        </div>
    </div>
    <BottomNavigation
        :back="back"
        :cancel="cancel"
        :confirm="sendEstimate"
        :confirmButtonDisabled="!formValid"
        :first="step === EstimateFormStep.first"
        :isEditing="isEditing"
        :last="step === EstimateFormStep.second"
        :next="next"
        :v-if="false"
    />
    <WaitingModal v-if="isLoading" :text="$t('estimate.request.pending')" />
    <SuccessModal v-if="success" :subtitle="$t('estimate.success.subtitle')" :title="response" @click="cancel" />
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import EstimateForm from '@/modules/Estimate/components/EstimateForm.vue';
import BottomNavigation from '@/components/layout/BottomNavigation.vue';
import SelectSite from '@/modules/SiteList/components/SelectSite.vue';
import { EstimateFormStep } from '@/modules/Estimate/store/state';
import { ESTIMATE } from '../store/mutation-types';
import { Site } from '@/models/Site';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import { EstimateModuleAction } from '../store/actions-types';
import SuccessModal from '@/components/ui-components/SuccessModal.vue';
import { SITE_LIST } from '@/modules/SiteList/store/mutation-types';
import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import ReglePastille from '@/components/regles/ReglePastille.vue';

export default defineComponent({
    name: 'EstimatePage',
    components: {
        EstimateForm,
        BottomNavigation,
        SelectSite,
        WaitingModal,
        SuccessModal,
        ReglePastille,
    },
    setup() {
        return {
            EstimateFormStep,
        };
    },

    computed: {
        step() {
            return this.$store.getters.getEstimateStepValue;
        },
        formValid() {
            return this.$store.getters.isEstimateFormValid;
        },
        selectedSite(): Site {
            return this.$store.getters.getSiteValue;
        },
        isLoading() {
            return this.$store.getters.getEstimateSendLoading;
        },
        success() {
            return this.$store.getters.getEstimateRequestSuccess;
        },
        response() {
            return this.$t('estimate.success.title', {
                estimateId: this.$store.getters.getEstimateRequestResponse.id,
            });
        },
        isEditing() {
            return this.$store.getters.getAskEstimateData?.isEditing;
        },
    },
    methods: {
        back() {
            this.$store.commit(ESTIMATE.SET_STEP, EstimateFormStep.first);
        },
        next() {
            this.$store.commit(ESTIMATE.SET_STEP, EstimateFormStep.second);
        },
        cancel() {
            this.$router.replace('/dashboard');
            this.reset();
        },
        selectSite(site: Site) {
            this.$store.commit(ESTIMATE.SET_CHANTIER, site);
        },
        sendEstimate() {
            if (this.isEditing) {
                this.$store.dispatch(EstimateModuleAction.UPDATE_ASK_ESTIMATE);
                return;
            }
            this.$store.dispatch(EstimateModuleAction.SEND_ASK_ESTIMATE);
        },
        reset() {
            this.$store.commit(`siteList/${SITE_LIST.RESET}`);
            this.$store.commit(`siteList/${SITE_LIST.SET_FILTER}`, '');
            this.$store.commit(ESTIMATE.RESET_ESTIMATE);
        },
    },
    beforeRouteLeave(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
        this.reset();
        next();
    },
});
</script>

<style scoped>
.page-content-container {
    padding: 24px;
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow-y: auto;
}
.estimate-form {
    padding: 24px;
    margin-bottom: 50px;
}
.estimate-content-container {
    box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px rgba(0, 0, 0, 0.14), 0 1px 5px rgba(0, 0, 0, 0.12);
    max-height: calc(100vh - 230px);
    background-color: var(--white);
    border-radius: 4px;
    margin-top: 16px;
}
h1.title {
    font-size: var(--font-size-high-large);
    font-family: 'Raleway Semi Bold', sans-serif !important;
    font-variant-numeric: lining-nums;
    color: var(--color-principal);
    margin: 0 0 8px 0;
}
h2.form-title {
    margin: 0;
    font-size: var(--font-size-medium);
    font-weight: 500;
    color: var(--color-principal);
}
p.subtitle {
    font-size: var(--font-size-low-medium);
    font-weight: var(--font-weight-normal);
    color: var(--dark-grey);
    margin-top: 8px;
    margin-bottom: 16px;
}
.subtitle-site {
    font-size: 16px;
    color: var(--dark-grey);
    font-weight: 400;
}
.contract-label {
    font-size: 12px;
}
</style>
